import TextInput from 'common/components/TextInput'
import theme from 'common/theme'
import EscapeRegEx from 'lib/escape-regex'
import highlighter from 'lib/highlighter'
import { useEffect, useState } from 'react'
import { Trans } from 'react-i18next'
import styled from 'styled-components'

interface IconType {
  icon: JSX.Element | null
  position: string
}
interface AuthCompleteType {
  id?: string
  autoFocus?: boolean
  disabled?: boolean
  placeholder?: string
  required?: boolean
  bordered?: boolean
  fontSize?: string
  fontWeight?: string
  onHoverFontColor?: string
  width?: string
  height?: string
  margin?: string
  maxLength?: string
  minLength?: string
  padding?: string
  backgroundColor?: string
  color?: string
  caretColor?: string
  helperText?: string
  hideHelperText?: boolean
  error?: boolean
  options?: { value: string | number; label: string }[]
  value?: string | number
  onblur?: Function
  onChange?: Function
  onfocus?: Function
  onHoverColor?: string
  onSelect?: Function
  withIcon?: IconType
  onTyping?: Function
  borderActiveColor?: string
  disabledItem?: boolean
  disabledLabel?: { value: string | number; label: string }[]
  showedValue?: string
  type?: 'default' | 'string' | 'number'
  hightlightLabel?: boolean
  rawValue?: boolean
}

interface AuthCompleteItemType {
  highlighted?: boolean
  value?: any
  label?: any
  onClick?: any
  onHoverColor?: string
  onHoverFontColor?: string
  onMouseOver?: any
  fontSize?: string
  disabledItem?: boolean
  hightlightLabel?: string
}

const AutocompleteWrapper = styled.div`
  position: relative;
  display: inline-flex;
  flex-direction: column;
  margin: ${(props) => props.margin};
  width: ${({ width }) => width};
`

const StyledList = styled.ul`
  position: absolute;
  top: ${(props) =>
    props.height !== 'auto' ? props.height : '43px'};
  border-radius: ${theme.radius.base};
  box-sizing: border-box;
  box-shadow: 0 0 0 0 rgba(6, 44, 82, 0.1);
  border: solid 1px ${theme.colors.grayLight};
  background-color: ${theme.colors.white};
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 188px;
  overflow-y: scroll;
  width: ${(props) => props.width};
  z-index: ${theme.zIndex.autoComplete};
`
const StyledListItem = styled.li`
  padding: 12px 16px;
  background-color: ${(props) =>
    props.highlighted && !props.disabledItem
      ? theme.colors[props.onHoverColor]
      : ''};
  font-size: ${(props) => props.fontSize};
  color: ${(props) =>
    props.highlighted && !props.disabledItem
      ? props.onHoverFontColor
      : theme.colors.black};
  z-index: ${theme.zIndex.autoComplete};
  :hover {
    background-color: ${(props) =>
      !props.disabledItem ? theme.colors[props.onHoverColor] : ''};
    color: ${(props) => props.onHoverFontColor};
  }
  cursor: ${(props) => (!props.disabledItem ? 'pointer' : 'default')};
`

function AutoCompleteItem({
  highlighted,
  value,
  label,
  onClick,
  onHoverColor,
  onMouseOver,
  fontSize,
  onHoverFontColor = theme.colors.white,
  disabledItem = false,
  hightlightLabel,
}: AuthCompleteItemType) {
  const onItemClick = () => {
    onClick(value)
  }
  const onItemMouseOver = () => {
    onMouseOver(value)
  }

  if (hightlightLabel) {
    label = <Trans>{highlighter(label, hightlightLabel)}</Trans>
  }
  return (
    <StyledListItem
      data-testid={`item-test-${label}`}
      highlighted={highlighted}
      onMouseDown={onItemClick}
      onHoverColor={onHoverColor}
      onMouseOver={onItemMouseOver}
      fontSize={fontSize}
      onHoverFontColor={onHoverFontColor}
      disabledItem={disabledItem}
    >
      {label}
    </StyledListItem>
  )
}

export default function AutoComplete({
  id,
  autoFocus = false,
  disabled,
  placeholder,
  required,
  bordered = true,
  fontSize = '16px',
  fontWeight = 'normal',
  onHoverFontColor = theme.colors.white,
  width = '100%',
  height = 'auto',
  margin = '',
  maxLength,
  minLength,
  padding = '18px',
  backgroundColor = `${theme.colors.white}`,
  color = `${theme.colors.black}`,
  caretColor = `${theme.colors.black}`,
  helperText = '',
  hideHelperText = false,
  error = false,
  value = '',
  options = [],
  onblur = () => null,
  onChange,
  onfocus = () => null,
  onHoverColor = 'orange',
  onSelect = () => null,
  withIcon = {
    icon: null,
    position: 'right',
  },
  onTyping,
  borderActiveColor = `${theme.colors.orange}`,
  disabledItem = false,
  disabledLabel,
  showedValue = '',
  type = 'default',
  hightlightLabel,
  rawValue,
}: AuthCompleteType): JSX.Element {
  const labelByValue = {}
  options.forEach((option) => {
    labelByValue[option.value] = option.label
  })
  const [mathesList, setMatchesList] = useState(options)
  const [currentValue, setCurrentValue] = useState(value)
  const valueLabel = options.find((item) => {
    return item.value === value
  })
  const defaultValueLabel = () => {
    if (valueLabel && showedValue === '') {
      return valueLabel.label
    }
    if (showedValue !== '') {
      return showedValue
    }
    return ''
  }
  const [typedLabel, setTypedLabel] = useState(defaultValueLabel())
  const [highlightedValue, setHighlightedValue] = useState(value)
  const [showItems, setShowItems] = useState(false)

  useEffect(() => {
    const matchesList = options.filter((item) => {
      return item.label.match(
        new RegExp(EscapeRegEx(typedLabel), 'i'),
      )
    })
    setMatchesList(matchesList)
    if (disabledItem) {
      setMatchesList(disabledLabel)
    }
  }, [JSON.stringify(options), typedLabel, disabledItem])

  const onAutocompleteChange = (val) => {
    setCurrentValue(val)
    if (onChange) {
      onChange(val)
    }
  }
  const onFocus = () => {
    onfocus()
    setShowItems(true)
  }
  const setFromHighlighted = () => {
    if (showedValue === '') {
      onAutocompleteChange(highlightedValue)
      setTypedLabel(
        highlightedValue
          ? labelByValue[highlightedValue]
          : typedLabel,
      )
    } else {
      setTypedLabel(showedValue)
    }
  }
  const onBlur = () => {
    onblur()
    if (typedLabel === '') {
      setCurrentValue('')
      setHighlightedValue('')
      setTypedLabel('')
    } else {
      setFromHighlighted()
    }
    setShowItems(false)
  }
  const onItemClick = (val) => {
    if (!disabledItem) {
      setTypedLabel(labelByValue[val])
      onAutocompleteChange(value)
      if (rawValue) {
        return onSelect({ label: labelByValue[val], value: val })
      }
      return onSelect(val)
    }
    return ''
  }
  const onItemMouseOver = (val) => {
    if (!disabledItem) {
      setHighlightedValue(val)
    }
  }
  const onMouseLeave = () => {
    setHighlightedValue(currentValue)
  }

  const renderMatches = () => {
    return mathesList.map((item) => {
      return (
        <AutoCompleteItem
          hightlightLabel={hightlightLabel ? typedLabel : null}
          highlighted={item.value === highlightedValue}
          key={item.value}
          label={item.label}
          value={item.value}
          onClick={onItemClick}
          onHoverColor={onHoverColor}
          onMouseOver={onItemMouseOver}
          fontSize={fontSize}
          onHoverFontColor={onHoverFontColor}
          disabledItem={disabledItem}
        />
      )
    })
  }

  const handleTyping = (e) => {
    let typing: string
    if (['default', 'string'].includes(type)) {
      typing = e.target.value
    }
    if (type === 'number') {
      typing = e.target.value.replace(/[^\d]/g, '')
    }
    if (onTyping) {
      onTyping(typing)
    }
    setTypedLabel(typing)
  }

  useEffect(() => setTypedLabel(labelByValue[value]), [value])

  let renderedList = renderMatches()
  useEffect(() => {
    renderedList = renderMatches()
  }, [mathesList])
  return (
    <AutocompleteWrapper margin={margin} width={width}>
      <TextInput
        data-testid="autocomplete-test"
        autoFocus={autoFocus}
        id={id}
        disabled={disabled}
        placeholder={placeholder}
        required={required}
        bordered={bordered}
        fontSize={fontSize}
        fontWeight={fontWeight}
        width={width}
        height={height}
        padding={padding}
        backgroundColor={backgroundColor}
        color={color}
        caretColor={caretColor}
        error={error}
        helperText={helperText}
        withIcon={withIcon}
        value={typedLabel}
        onChange={handleTyping}
        onFocus={onFocus}
        onBlur={onBlur}
        hideHelperText={hideHelperText}
        minLength={minLength}
        maxLength={maxLength}
        borderActiveColor={borderActiveColor}
      />
      {showItems ? (
        <StyledList
          data-testid="items-test"
          onMouseLeave={onMouseLeave}
          width={width}
          height={height}
        >
          {renderedList}
        </StyledList>
      ) : null}
    </AutocompleteWrapper>
  )
}
